<template>
    <!-- the score per quarter: a row per team, the better quarter in mint, the final score at the end -->
    <div class="period-score">
        <div class="period-score__grid" :style="{ gridTemplateColumns: `minmax(0, 1fr) repeat(${periods.length}, var(--cell)) var(--total)` }">
            <span class="period-score__head period-score__head--team">Ομάδα</span>
            <span v-for="period in periods" :key="`head-${period}`" class="period-score__head" :class="{ 'period-score__head--current': period === livePeriod }">
                <i v-if="period === livePeriod" class="period-score__dot" />{{ period > 4 ? `OT${period - 4}` : `Q${period}` }}
            </span>
            <span class="period-score__head">Τελ.</span>

            <template v-for="row in rows">
                <router-link :key="`team-${row.key}`" :to="{ name: 'Team', params: { id: row.team.id } }" class="period-score__team">
                    <span class="cl-logo period-score__logo">
                        <img v-if="row.team.logo" :src="`${assetsUrl}/${row.team.logo}`" alt="" @error="$event.target.style.visibility = 'hidden'">
                    </span>
                    <span class="period-score__name">{{ row.team.name }}</span>
                </router-link>
                <span
                    v-for="period in periods"
                    :key="`${row.key}-${period}`"
                    class="period-score__cell"
                    :class="{ 'period-score__cell--won': won(row.scores[period], row.other[period]), 'period-score__cell--current': period === livePeriod }"
                >
                    {{ row.scores[period] ?? '-' }}
                </span>
                <span :key="`total-${row.key}`" class="period-score__total" :class="{ 'period-score__total--won': row.winner && !live, 'period-score__total--live': live }">
                    {{ row.total ?? '-' }}
                </span>
            </template>
        </div>
    </div>
</template>

<script>
    export default {
        props: {
            periodScoresTeam1: {
                type: Object,
                default: () => ({ 1: '-', 2: '-', 3: '-', 4: '-' })
            },
            periodScoresTeam2: {
                type: Object,
                default: () => ({ 1: '-', 2: '-', 3: '-', 4: '-' })
            },
            team1: {
                type: Object,
                default: () => ({ name: 'Team 1' })
            },
            team2: {
                type: Object,
                default: () => ({ name: 'Team 2' })
            },
            score1: {
                type: Number,
                default: null
            },
            score2: {
                type: Number,
                default: null
            },
            live: {
                type: Boolean,
                default: false
            },
            // the period being played, while live
            currentPeriod: {
                type: Number,
                default: null
            }
        },
        data: () => ({
            assetsUrl: 'https://storage.googleapis.com'
        }),
        computed: {
            periods() {
                const normalize = obj =>
                    Object.keys(obj || {})
                        .map(k => Number(k))
                        .filter(n => Number.isFinite(n) && n > 0)
                        .sort((a, b) => a - b)

                const k1 = normalize(this.periodScoresTeam1)
                const k2 = normalize(this.periodScoresTeam2)

                if (!k1.length && !k2.length) return []

                if (k1.length < k2.length) return k1
                if (k2.length < k1.length) return k2

                // Same length: keep only common periods
                const set2 = new Set(k2)
                const inter = k1.filter(n => set2.has(n))
                return inter.length ? inter : k1
            },
            // while live, the latest period with points or in the play-by-play
            livePeriod() {
                if (!this.live) return null
                const scored = this.periods.filter(period => (Number(this.periodScoresTeam1[period]) || 0) + (Number(this.periodScoresTeam2[period]) || 0) > 0)
                const last = scored.length ? scored[scored.length - 1] : 1
                return Math.min(Math.max(last, Number(this.currentPeriod) || 0), this.periods[this.periods.length - 1] || 1)
            },
            rows() {
                const s1 = this.score1
                const s2 = this.score2
                const decided = s1 !== null && s2 !== null && s1 !== s2
                return [
                    { key: 1, team: this.team1 || {}, scores: this.periodScoresTeam1, other: this.periodScoresTeam2, total: s1, winner: decided && s1 > s2 },
                    { key: 2, team: this.team2 || {}, scores: this.periodScoresTeam2, other: this.periodScoresTeam1, total: s2, winner: decided && s2 > s1 }
                ]
            }
        },
        methods: {
            won(a, b) {
                return (Number(a) || 0) > (Number(b) || 0)
            }
        }
    }
</script>

<style scoped>
.period-score {
    --cell: 64px;
    --total: 84px;

    padding: 6px 18px 12px;
    border-radius: 12px;
    background: #232323;
    color: #fff;
    box-shadow: 0 8px 24px rgba(35, 35, 35, 0.12);
    font-family: Roboto, Arial, sans-serif;
    font-variant-numeric: tabular-nums;
}

.period-score__grid {
    display: grid;
    align-items: center;
}

.period-score__head {
    padding: 10px 0;
    border-bottom: 2px solid rgba(3, 229, 183, 0.35);
    color: #03e5b7;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-align: center;
    text-transform: uppercase;
}

.period-score__head--team {
    text-align: left;
}

.period-score__team {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    padding: 12px 12px 12px 0;
    color: #fff !important;
    text-decoration: none;
}

.period-score__team:hover .period-score__name {
    color: #03e5b7;
}

.period-score__logo {
    width: 42px;
    height: 42px;
}

.period-score__name {
    overflow: hidden;
    font-weight: 700;
    font-size: 0.95rem;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.period-score__cell,
.period-score__total {
    text-align: center;
    font-size: 1.15rem;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.55);
}

.period-score__cell--won {
    color: #03e5b7;
}

.period-score__total {
    justify-self: center;
    min-width: 62px;
    padding: 6px 0;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.8rem;
    font-weight: 400;
    line-height: 1;
}

.period-score__total--won {
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
}

.period-score__head--current {
    color: #ff5252;
}

.period-score__dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    margin-right: 4px;
    border-radius: 50%;
    background: #ff5252;
    vertical-align: middle;
    animation: period-live 1.2s ease-in-out infinite;
}

.period-score__cell--current {
    color: #fff;
}

.period-score__total--live {
    background: transparent;
    box-shadow: inset 0 0 0 2px #ff5252;
}

@keyframes period-live {
    0%,
    100% { opacity: 1; }
    50% { opacity: 0.3; }
}

@media (max-width: 600px) {
    .period-score {
        --cell: 34px;
        --total: 54px;

        padding: 4px 10px 10px;
    }

    .period-score__logo {
        width: 32px;
        height: 32px;
    }

    .period-score__team {
        gap: 8px;
        padding-right: 6px;
    }

    .period-score__name {
        font-size: 0.8rem;
    }

    .period-score__cell {
        font-size: 0.95rem;
    }

    .period-score__total {
        min-width: 44px;
        font-size: 1.4rem;
    }
}
</style>
